import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { CustomFieldObject } from '~/models/customData';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import { queryKeysCustomField } from './queryKeys';
import { type CustomFieldsListItem } from './useQueryCustomFields';

export type CustomField = CustomFieldsListItem;

type CustomFieldResponse = CustomField;

const zodCustomFieldQueryParamsSchema = z
  .object({
    language: z.string().nullish(),
  })
  .transform((data) => withoutObjectKeysWhereValueIs(data, [null, undefined]));

export type CustomFieldQueryParams = {
  language?: string;
};

/**
 * Fetches all CustomFields from the API.
 *
 * @param customFieldId - The id of the CustomField to fetch.
 * @returns The custom field data.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/get_custom_field_custom_field__custom_field_id__get
 */
export const fetchCustomField = async (
  customFieldId: UUID,
  queryParams: Partial<CustomFieldQueryParams>,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CUSTOM_FIELD.GET(customFieldId), {
      searchParams: queryParams,
      validate: {
        searchParams: zodCustomFieldQueryParamsSchema,
      },
    })
    .json<CustomFieldResponse>();

  return response;
};

/**
 * React Query based custom hook for getting the data for a CustomField with the given id.
 *
 * @param customFieldId - The id of the CustomField to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/get_custom_field_custom_field__custom_field_id__get
 */
export const useQueryCustomField = (
  {
    customFieldId,
    queryParams,
  }: {
    customFieldId: UUID;
    queryParams?: Partial<CustomFieldQueryParams>;
  },
  options?: Parameters<typeof useQuery>[0],
) => {
  return useQuery<CustomFieldResponse>({
    enabled: Boolean(customFieldId),
    queryFn: async () => fetchCustomField(customFieldId, queryParams),
    queryKey: queryKeysCustomField.get(customFieldId),
    select: (data) => CustomFieldObject.create(data),
    ...options,
  });
};
